import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'

export interface GlintRevealOptions {
  startX?: string
  startY?: string
  endX?: string
  endY?: string
  rotate?: number
  width?: string
  height?: string
  blur?: number
}

export interface GlintRevealProps {
  active: boolean
  glintDuration?: number
  trigger?: number
  onComplete?: () => void
  options?: GlintRevealOptions
}

const GLINT_ID = 'glint-reveal-gradient'

const createGradient = () => (
  <svg width="0" height="0" style={{ position: 'absolute', pointerEvents: 'none' }}>
    <defs>
      <linearGradient id={GLINT_ID} x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="rgba(255, 214, 130, 0)" />
        <stop offset="35%" stopColor="rgba(255, 214, 130, 0.45)" />
        <stop offset="55%" stopColor="rgba(255, 229, 173, 0.95)" />
        <stop offset="72%" stopColor="rgba(255, 186, 90, 0.75)" />
        <stop offset="100%" stopColor="rgba(255, 214, 130, 0)" />
      </linearGradient>
    </defs>
  </svg>
)

const DEFAULT_OPTIONS: Required<GlintRevealOptions> = {
  startX: '-100%',
  startY: '-100%',
  endX: '140%',
  endY: '140%',
  rotate: -50,
  width: '280%',
  height: '22%',
  blur: 0.6
}

export const GlintReveal: React.FC<GlintRevealProps> = ({
  active,
  glintDuration = 2.4,
  trigger = 0,
  onComplete,
  options
}) => {
  const [gradientAdded, setGradientAdded] = React.useState(false)
  const mergedOptions = React.useMemo(() => ({ ...DEFAULT_OPTIONS, ...options }), [options])

  React.useEffect(() => {
    if (gradientAdded) return
    setGradientAdded(true)
  }, [gradientAdded])

  return (
    <>
      {gradientAdded ? null : createGradient()}
      <AnimatePresence>
        {active && (
          <motion.div
            key={trigger}
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.24, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: 'inherit',
              overflow: 'hidden',
              pointerEvents: 'none'
            }}
          >
            <motion.div
              initial={{ x: mergedOptions.startX, y: mergedOptions.startY, rotate: mergedOptions.rotate }}
              animate={{
                x: [mergedOptions.startX, mergedOptions.endX],
                y: [mergedOptions.startY, mergedOptions.endY],
                rotate: mergedOptions.rotate
              }}
              transition={{
                duration: glintDuration,
                ease: [0.33, 0.01, 0.18, 0.99]
              }}
              onAnimationComplete={onComplete}
              style={{
                position: 'absolute',
                width: mergedOptions.width,
                height: mergedOptions.height,
                background: `url(#${GLINT_ID})`,
                backgroundImage: [
                  'radial-gradient(circle at center, rgba(255, 232, 176, 0.85) 0%, rgba(255, 201, 120, 0.65) 32%, rgba(255, 171, 64, 0.15) 68%, rgba(255, 171, 64, 0) 100%)',
                  'linear-gradient(135deg, rgba(255, 195, 102, 0) 0%, rgba(255, 201, 120, 0.55) 40%, rgba(255, 237, 189, 0.98) 58%, rgba(255, 195, 102, 0.85) 78%, rgba(255, 214, 130, 0) 100%)'
                ].join(', '),
                boxShadow: '0px 0px 36px rgba(255, 208, 124, 0.62), 0px 0px 18px rgba(255, 238, 194, 0.45)',
                opacity: 1,
                filter: `blur(${mergedOptions.blur}px)`,
                mixBlendMode: 'screen'
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default GlintReveal
